import { useCallback } from 'react';
import { usePaymentApi } from '../paymentApiContext';
import logger from '../../../../utils/logging/logger';
import { computeCurrentTotalAmount } from './pricing';
import { handleFreeEnrollmentSuccess, handleCreatePaymentIntentError } from './createPaymentIntentHelpers';

// createPaymentIntent, extracted VERBATIM (its two largest branches — the
// free-enrollment success handling and the catch-block error handling — are
// factored out to createPaymentIntentHelpers.js so this file stays under the
// line-count cap; the control flow here is otherwise unchanged).
export function useCreatePaymentIntent({
  productIds,
  appliedPromo,
  bulkPricing,
  onClientSecretChange,
  processing,
  error,
  stripeConfig,
  loading,
  courses,
  coursesRef,
  appliedPromoRef,
  bulkPricingRef,
  productIdsRef,
  clientSecret,
  setClientSecret,
  setProcessing,
  setError,
  setPaymentIntentCreated,
  paymentIntentCreatedRef,
  paymentIntentCreationLockRef,
  initialPaymentIntentAttemptedRef,
  lastPaymentIntentAttemptRef,
  apiCallTimestampsRef,
  RATE_LIMIT_DELAY,
  navigate,
}) {
  const paymentApi = usePaymentApi(); // signed-in api, or the guest adapter for new students
  const createPaymentIntent = useCallback(async (forceRecreate = false) => {
    // FIX: Use ref to check current value (avoids dependency on paymentIntentCreated)
    if (paymentIntentCreatedRef.current && !forceRecreate) {
      logger.debug('Payment intent already created, skipping');
      return;
    }

    // CRITICAL FIX #1: Prevent concurrent payment intent creation using mutex
    if (paymentIntentCreationLockRef.current) {
      logger.debug('Payment intent creation locked (already in progress), skipping');
      return;
    }

    // Prevent concurrent payment intent creation
    if (processing) {
      logger.debug('Payment intent creation already in progress, skipping');
      return;
    }

    // FIX #31: Rate limiting - prevent rapid API calls
    const now = Date.now();
    const lastCallTime = apiCallTimestampsRef.current.createPaymentIntent;
    if (lastCallTime && (now - lastCallTime) < RATE_LIMIT_DELAY) {
      logger.debug('Payment intent creation rate limited, skipping');
      return;
    }
    apiCallTimestampsRef.current.createPaymentIntent = now;

    // Prevent rapid retries (debounce) - wait at least 2 seconds between attempts
    if (lastPaymentIntentAttemptRef.current && (now - lastPaymentIntentAttemptRef.current) < 2000) {
      logger.debug('Payment intent creation debounced, skipping');
      return;
    }
    lastPaymentIntentAttemptRef.current = now;

    // FIX #39: Simplified error blocking logic - only block if error exists and not forcing recreation
    // User can retry by applying/removing promo code or refreshing
    // Removed redundant check - single condition is sufficient
    if (error && !forceRecreate) {
      logger.debug('Payment intent creation blocked due to existing error (user can retry):', error);
      return;
    }

    // FIX: Use ref to get current productIds value FIRST to avoid stale closures
    // This must be done early so we can use it consistently throughout the function
    const currentProductIds = productIdsRef.current || productIds;

    // FIX: For products, ensure we have valid courses data before creating payment intent
    // If productIds exists but courses is empty, don't create payment intent (product fetch likely failed)
    // CRITICAL: Also check error state and loading state to prevent race conditions
    if (currentProductIds) {
      const currentCourses = coursesRef.current || courses;
      const currentError = error; // Check current error state
      const currentLoading = loading; // Check current loading state

      // Block if still loading AND we don't have courses data in ref or state
      // If we have courses in the ref, we can proceed even if loading is true (data restored from sessionStorage)
      const hasCoursesData = (currentCourses && currentCourses.length > 0) || (coursesRef.current && coursesRef.current.length > 0);
      if (currentLoading && !hasCoursesData) {
        logger.debug('Payment intent creation blocked: products are still loading', {
          productIds: currentProductIds
        });
        return;
      }

      // Block if there's an error (product fetch failed)
      if (currentError) {
        logger.debug('Payment intent creation blocked: error exists (product fetch likely failed)', {
          productIds: currentProductIds,
          error: currentError
        });
        return;
      }

      // Block if courses array is empty (product fetch failed or not completed)
      if (!currentCourses || !Array.isArray(currentCourses) || currentCourses.length === 0) {
        logger.debug('Payment intent creation blocked: products expected but courses array is empty', {
          productIds: currentProductIds,
          coursesLength: currentCourses?.length || 0,
          loading: currentLoading,
          error: currentError
        });
        return;
      }
    }

    // Acquire lock
    paymentIntentCreationLockRef.current = true;

    // FIX #5: Use single source of truth - getTotalPrice() function
    // FIX: Use refs to get current state values to avoid stale closures
    const currentAppliedPromo = appliedPromoRef.current || appliedPromo;
    const currentBulkPricing = bulkPricingRef.current || bulkPricing;

    // Calculate total with current state values (not stale closure values)
    // FIX: Use refs to get current values to avoid dependency on courses array reference
    // FIX: Use currentProductIds instead of productIds from closure
    const currentCoursesForTotal = coursesRef.current || courses;
    const totalAmount = computeCurrentTotalAmount({
      courses: currentCoursesForTotal,
      appliedPromo: currentAppliedPromo,
      bulkPricing: currentBulkPricing,
      productIds: currentProductIds,
    });

    console.log('[FREE_ENROLLMENT] Final totalAmount calculated:', {
      totalAmount,
      isZero: totalAmount === 0,
      isNaN: isNaN(totalAmount),
      isLessThanZero: totalAmount < 0
    });

    // CRITICAL FIX #2: Validate payment amount before creating payment intent
    // Allow $0 for free enrollments (100% promo code discount)
    if (totalAmount === undefined || totalAmount === null || isNaN(totalAmount) || totalAmount < 0) {
      console.error('[FREE_ENROLLMENT] ❌ Invalid totalAmount - rejecting', {
        totalAmount,
        type: typeof totalAmount,
        isNaN: isNaN(totalAmount),
        isLessThanZero: totalAmount < 0
      });
      const currentCourses = coursesRef.current || courses;
      logger.error('Invalid payment amount calculated', {
        totalAmount,
        courses: currentCourses.map(c => ({ id: c.id, name: c.name, price: c.price })),
        appliedPromo: currentAppliedPromo,
        bulkPricing: currentBulkPricing ? currentBulkPricing.pricing : null,
        productIds: currentProductIds
      });
      paymentIntentCreationLockRef.current = false; // Release lock
      setError('Invalid payment amount. Please ensure courses have valid prices.');
      setProcessing(false);
      return;
    }
    // Note: totalAmount === 0 is allowed for free enrollments (handled by backend)

    if (totalAmount === 0) {
      console.log('[FREE_ENROLLMENT] ✅ totalAmount is $0 - backend will skip Stripe and create enrollments directly');
      logger.info('Free enrollment detected - backend will skip Stripe payment intent creation');
    }

    // FIX: Get current courses for API call (currentAppliedPromo and currentBulkPricing already declared above)
    const currentCourses = coursesRef.current || courses;

    console.log('[FREE_ENROLLMENT] About to call backend API:', {
      forceRecreate,
      totalAmount,
      isZero: totalAmount === 0,
      courseIds: currentCourses.map(c => c.id),
      courseCount: currentCourses.length,
      hasPromo: !!currentAppliedPromo,
      promoCode: currentAppliedPromo?.code,
      note: totalAmount === 0
        ? 'Backend endpoint will detect $0 and skip Stripe, creating enrollments directly'
        : 'Backend endpoint will create Stripe payment intent'
    });

    logger.debug('Creating payment intent', {
      forceRecreate,
      totalAmount,
      appliedPromo: currentAppliedPromo,
      bulkPricing: currentBulkPricing ? currentBulkPricing.pricing : null,
      productIds: currentProductIds,
      productIdsFromParams: productIds,
      productIdsFromRef: productIdsRef.current
    });

    setProcessing(true);
    setError(null);

    // CRITICAL FIX #4: Don't clear client secret when recreating - this causes remount loops
    // Instead, we'll update the payment intent with the new amount
    // The Stripe Elements component can handle payment intent updates without remounting
    // Only clear if we're forcing a complete reset (which shouldn't happen during normal discount changes)
    // if (forceRecreate && !processing && clientSecret) {
    //   setClientSecret(null);
    //   onClientSecretChange?.(null);
    // }

    try {
      let response;

      // FIX: Use ref value to ensure we have the latest productIds
      // CRITICAL: Check both currentProductIds AND productIds from params to catch any edge cases
      const hasProductIds = !!(currentProductIds || productIds);

      if (hasProductIds) {
        // FIX: Use the most reliable source - prefer currentProductIds from ref, fallback to productIds from params
        const productIdsToUse = currentProductIds || productIds;
        // FIX: Use current state values from refs to avoid stale closures
        // FIX: Use productIdsToUse to ensure we have the latest value
        const productIdsArray = productIdsToUse.split(',').map(id => parseInt(id.trim())).filter(id => !isNaN(id));

        // CRITICAL: Double-check we're not accidentally treating products as courses
        if (productIdsArray.length === 0) {
          logger.error('⚠️ Product IDs array is empty after parsing', {
            productIdsToUse,
            currentProductIds,
            productIdsFromParams: productIds
          });
          throw new Error('Invalid product IDs');
        }

        logger.info('🛒 Creating PRODUCT payment intent', {
          productIds: productIdsArray,
          totalAmount,
          hasPromoCode: !!currentAppliedPromo,
          productIdsSource: currentProductIds ? 'ref' : 'params'
        });
        response = await paymentApi.createProductPaymentIntent({
          productIds: productIdsArray,
          totalAmount,
          promoCode: currentAppliedPromo
        });
      } else {
        // FIX: This is the COURSE route - ensure we're not accidentally routing products here
        // Log warning if we detect products in courses data but no productIds
        const hasProductTypeInCourses = currentCourses.some(c => c.type === 'product');
        if (hasProductTypeInCourses) {
          logger.error('⚠️ CRITICAL: Products detected in courses array but no productIds in params/ref - routing to COURSE endpoint incorrectly!', {
            courses: currentCourses.map(c => ({ id: c.id, name: c.name, type: c.type })),
            productIdsFromParams: productIds,
            productIdsFromRef: productIdsRef.current,
            currentProductIds
          });
        }

        const paymentRequest = {
          totalAmount,
          // Only send bulk pricing if no promo code is applied (mutually exclusive)
          // FIX: Use current state values from refs to avoid stale closures
          bulkPricing: currentAppliedPromo ? null : currentBulkPricing,
          promoCode: currentAppliedPromo,
          courseIds: currentCourses.map(course => parseInt(course.id)) // Ensure course IDs are integers
        };

        // Debug: Log what we're sending to the backend
        logger.info('💳 Creating COURSE payment intent with:', {
          totalAmount,
          hasPromoCode: !!currentAppliedPromo,
          promoCode: currentAppliedPromo ? {
            code: currentAppliedPromo.code,
            discount_type: currentAppliedPromo.discount_type,
            discount_value: currentAppliedPromo.discount_value
          } : null,
          hasBulkPricing: !!currentBulkPricing,
          courseIds: paymentRequest.courseIds,
          courseCount: currentCourses.length
        });

        logger.debug('Payment request data', {
          totalAmount,
          hasPromoCode: !!appliedPromo,
          promoCode: appliedPromo ? {
            code: appliedPromo.code,
            discount_type: appliedPromo.discount_type,
            discount_value: appliedPromo.discount_value
          } : null,
          bulkPricing: appliedPromo ? null : (bulkPricing ? {
            discountAmount: bulkPricing.pricing?.discountAmount,
            finalPrice: bulkPricing.pricing?.finalPrice
          } : null),
          courseIds: paymentRequest.courseIds,
          baseTotal: (courses && Array.isArray(courses) && courses.length > 0) ? courses.reduce((sum, course) => {
            if (!course) return sum;
            const price = (course.isPaidCourse || course.is_paid_course) ? (parseFloat(course.price) || 0) : 0;
            return Math.round((sum + price) * 100) / 100;
          }, 0) : 0
        });
        console.log('[FREE_ENROLLMENT] 📤 Making API call to createPaymentIntent with:', {
          totalAmount: paymentRequest.totalAmount,
          isZero: paymentRequest.totalAmount === 0,
          courseIds: paymentRequest.courseIds,
          hasPromoCode: !!paymentRequest.promoCode,
          promoCode: paymentRequest.promoCode?.code,
          hasBulkPricing: !!paymentRequest.bulkPricing
        });

        response = await paymentApi.createPaymentIntent(paymentRequest);

        console.log('[FREE_ENROLLMENT] 📥 API Response received:', {
          success: response?.success,
          freeEnrollment: response?.freeEnrollment,
          redirectTo: response?.redirectTo,
          amount: response?.amount,
          paymentIntentId: response?.paymentIntentId,
          hasClientSecret: !!response?.clientSecret,
          hasCourses: !!response?.courses,
          courseCount: response?.courses?.length,
          hasUser: !!response?.user,
          message: response?.message,
          responseType: typeof response,
          responseKeys: response ? Object.keys(response) : []
        });
      }

      // FIX #31: Never log clientSecret - only log payment intent ID (security fix)
      // Removed any potential clientSecret logging
      console.log('[FREE_ENROLLMENT] ✅ API Response received:', {
        success: response.success,
        freeEnrollment: response.freeEnrollment,
        redirectTo: response.redirectTo,
        amount: response.amount,
        paymentIntentId: response.paymentIntentId,
        hasClientSecret: !!response.clientSecret,
        hasCourses: !!response.courses,
        courseCount: response.courses?.length,
        hasUser: !!response.user,
        message: response.message,
        totalAmount,
        responseKeys: Object.keys(response || {}),
        paymentMethodConfiguration: response.paymentMethodConfiguration || 'NOT PROVIDED IN RESPONSE'
      });
      console.log('💳 PAYMENT METHOD CONFIGURATION:', stripeConfig?.stripePaymentMethodConfiguration || 'NOT CONFIGURED');

      logger.debug('Payment intent created', {
        paymentIntentId: response.paymentIntentId || 'unknown',
        totalAmount,
        hasClientSecret: !!response.clientSecret, // Only log existence, not value
        redirectTo: response.redirectTo,
        appliedPromo: appliedPromo ? { code: appliedPromo.code, discount_type: appliedPromo.discount_type } : null,
        bulkPricing: bulkPricing ? { discountAmount: bulkPricing.pricing?.discountAmount } : null
      });

      // Check if this is a free enrollment ($0 total) - backend returns freeEnrollment: true or redirectTo: 'confirmation'
      const navigatedAsFreeEnrollment = handleFreeEnrollmentSuccess(response, totalAmount, {
        navigate,
        setProcessing,
        paymentIntentCreatedRef,
        paymentIntentCreationLockRef,
      });
      if (navigatedAsFreeEnrollment) {
        return; // Don't set up payment form for free enrollments
      }

      // CRITICAL: Set refs BEFORE updating clientSecret to prevent race conditions
      // This ensures that if the component remounts due to clientSecret change,
      // the refs are already set and prevent duplicate payment intent creation
      setPaymentIntentCreated(true);
      paymentIntentCreatedRef.current = true; // Update ref immediately BEFORE clientSecret change
      initialPaymentIntentAttemptedRef.current = true; // Mark as attempted

      // FIX: Only update clientSecret if it actually changed to prevent unnecessary remounts
      // CRITICAL: When forceRecreate is true, we MUST update the clientSecret and notify parent
      // so that Stripe Elements uses the new payment intent (with the correct discounted amount)
      if (response.clientSecret !== clientSecret) {
        setClientSecret(response.clientSecret);
        // Always notify parent when clientSecret changes, even on forceRecreate
        // This ensures Stripe Elements uses the new payment intent with the correct amount
        // The parent will handle remounting Elements if needed
        onClientSecretChange?.(response.clientSecret);
        if (forceRecreate) {
          logger.info('🔄 Payment intent recreated with new amount', {
            oldAmount: clientSecret ? 'existing' : 'none',
            newAmount: totalAmount,
            hasPromoCode: !!currentAppliedPromo,
            promoCode: currentAppliedPromo?.code
          });
        }
      }
      setProcessing(false);
      paymentIntentCreationLockRef.current = false; // Release lock on success
      return true;
    } catch (error) {
      const handledAsFreeEnrollment = handleCreatePaymentIntentError(error, totalAmount, {
        navigate,
        setError,
        setProcessing,
        lastPaymentIntentAttemptRef,
        paymentIntentCreatedRef,
        paymentIntentCreationLockRef,
      });
      if (handledAsFreeEnrollment) {
        return;
      }
      return false;
    }
  // FIX #36: Optimize dependencies - use refs for values that don't need to trigger recreation
  // Note: getTotalPrice is not needed as a dependency since createPaymentIntent calculates total directly using refs
  // CRITICAL FIX: Remove paymentIntentCreated from dependencies to prevent infinite loop
  // paymentIntentCreated is only used in a guard clause via paymentIntentCreatedRef, not directly
  // CRITICAL FIX: Remove courses and bulkPricing from dependencies - they cause recreation on every state update
  // Use refs to access current values instead (coursesRef, bulkPricingRef)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productIds, appliedPromo, onClientSecretChange, processing, error, stripeConfig?.stripePaymentMethodConfiguration]);

  return { createPaymentIntent };
}
